import SEOHead from "@/components/base/SEOHead";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import WhatsAppFloat from "@/components/feature/WhatsAppFloat";
import AveriasFrecuentesSection from "@/components/feature/AveriasFrecuentesSection";
import FAQSection from "@/components/feature/FAQSection";
import ComoTrabajamosSection from "@/components/feature/ComoTrabajamosSection";
import Breadcrumb from "@/components/feature/Breadcrumb";
import PricingSection from "@/components/feature/PricingSection";
import TrustBannerSection from "@/components/feature/TrustBannerSection";
import { preciosPorServicio } from "@/mocks/precios";

export default function MosquiterasPage() {
  return (
    <div className="min-h-screen bg-background-50">
      <SEOHead
        title="Mosquiteras a Medida Málaga | Persianas Málaga"
        description="Mosquiteras a medida en Málaga: enrollables, fijas, correderas y plisadas. Fabricación e instalación profesional. Presupuesto gratis, 2 años de garantía."
      />
      <Navbar />

      <Breadcrumb items={[
        { label: "Inicio", path: "/" },
        { label: "Servicios" },
        { label: "Mosquiteras" },
      ]} />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "Mosquiteras a Medida en Málaga",
        "provider": { "@type": "LocalBusiness", "name": "Persianas Málaga", "telephone": "+34 951 12 07 70" },
        "areaServed": "Málaga capital",
        "description": "Mosquiteras a medida en Málaga: enrollables, fijas, correderas y plisadas. Fabricación a medida e instalación profesional en todos los barrios.",
        "serviceType": "Instalación de Mosquiteras",
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "Servicios de mosquiteras",
          "itemListElement": [
            { "@type": "Offer", "name": "Mosquitera enrollable a medida", "price": "60", "priceCurrency": "EUR" },
            { "@type": "Offer", "name": "Revisión y mantenimiento", "price": "30", "priceCurrency": "EUR" }
          ]
        }
      }) }} />

      <section className="py-14 md:py-20 bg-gradient-to-br from-background-50 via-primary-50/30 to-accent-50/30">
        <div className="px-4 md:px-6 max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-8 md:gap-12 items-center">
            <div className="flex-1 w-full">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground-950 mb-4 leading-tight font-heading">
                Mosquiteras a medida <span className="text-primary-500">en Málaga</span>
              </h1>
              <p className="text-sm md:text-lg text-foreground-600 mb-6 max-w-lg leading-relaxed">
                Mosquiteras enrollables, fijas, correderas y plisadas. Fabricación a medida e instalación profesional en todos los barrios de Málaga. Presupuesto gratis, 2 años de garantía.
              </p>
              <a href="tel:+34951120770" className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-background-50 font-bold text-sm md:text-base px-8 py-4 rounded-full transition-all active:scale-[0.98] whitespace-nowrap cursor-pointer shadow-lg shadow-primary-500/20">
                <span className="w-5 h-5 flex items-center justify-center"><i className="ri-phone-fill"></i></span>
                951 12 07 70
              </a>
            </div>
            <div className="w-full lg:w-[45%] h-64 sm:h-80 md:h-96 rounded-2xl overflow-hidden flex-shrink-0">
              <img alt="Instalación de mosquiteras a medida en Málaga" className="w-full h-full object-cover object-top" loading="eager" src="/images/014-serv-mosquiteras-hero-v2.webp" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-10 md:py-14 bg-background-100">
        <div className="px-4 md:px-6 max-w-7xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-8 font-heading text-center">Tipos de mosquiteras</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {[
              { icon: "ri-arrow-up-down-line", title: "Enrollables", desc: "Las más populares. Se recogen en un cajetín superior. Ideales para ventanas y puertas. Perfiles de aluminio lacado. Discretas y funcionales." },
              { icon: "ri-checkbox-blank-line", title: "Fijas", desc: "Marco fijo de aluminio con malla. Solución económica y duradera para ventanas que no se abren o se abren poco. Fácil mantenimiento." },
              { icon: "ri-arrow-left-right-line", title: "Correderas", desc: "Perfectas para puertas de terraza y balcones. Se deslizan lateralmente sobre guías. Malla de fibra de vidrio de alta resistencia." },
              { icon: "ri-layout-masonry-line", title: "Plisadas", desc: "Sistema de fuelle que se pliega hacia arriba. Sin cajetín. Muy estéticas. Ideales para ventanas grandes con mucha luz natural." },
              { icon: "ri-door-line", title: "Para puertas", desc: "Mosquiteras específicas para puertas de entrada y salida a terrazas. Cierre imantado. Medición profesional e instalación precisa." },
              { icon: "ri-paint-brush-line", title: "A medida y color", desc: "Fabricamos mosquiteras a la medida exacta de tus ventanas y puertas. Disponibles en blanco, negro y acabados lacados para combinar." },
            ].map((item) => (
              <div key={item.title} className="p-5 md:p-6 bg-background-50 rounded-2xl border border-background-200/60">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-secondary-500/10 mb-3">
                  <span className="w-5 h-5 flex items-center justify-center"><i className={`${item.icon} text-secondary-600 text-lg`}></i></span>
                </div>
                <h3 className="text-sm md:text-base font-bold text-foreground-900 mb-1.5">{item.title}</h3>
                <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <AveriasFrecuentesSection ids={["mosquitera-rota"]} />

      <FAQSection
        title="Preguntas frecuentes sobre mosquiteras"
        questions={[
          {
            question: "¿Cuánto cuesta una mosquitera a medida en Málaga?",
            answer: "Depende del tipo y tamaño. Mosquiteras enrollables: desde 75€ para ventana estándar. Correderas para puertas de terraza: desde 130€. Fijas: desde 55€. Plisadas: desde 110€. Todos los precios incluyen medición, fabricación a medida e instalación. El presupuesto exacto te lo damos gratis tras medir tus ventanas, sin compromiso.",
          },
          {
            question: "¿Se puede reparar una mosquitera enrollable que no recoge?",
            answer: "Sí, en la mayoría de los casos. El problema suele estar en el muelle del cajetín, que pierde fuerza con el tiempo. Se cambia el muelle por uno nuevo y la mosquitera vuelve a funcionar perfectamente. La reparación cuesta entre 35€ y 60€, mucho menos que una mosquitera nueva. Si la malla también está rota, la cambiamos en la misma visita.",
          },
          {
            question: "¿Se pueden instalar mosquiteras en ventanas de aluminio y PVC?",
            answer: "Sí, instalamos mosquiteras en todo tipo de ventanas: aluminio, PVC y madera. Cada material requiere un sistema de fijación diferente, y nosotros tenemos experiencia con todos. La instalación no daña la ventana ni afecta a su funcionamiento. Para ventanas con persiana incorporada, hay soluciones específicas que no interfieren con el cajetín.",
          },
          {
            question: "¿Cuánto duran las mosquiteras en zonas de costa como Málaga?",
            answer: "Una mosquitera de calidad, con malla de fibra de vidrio y perfiles de aluminio lacado, dura entre 8 y 12 años en Málaga. Las que están en ventanas con sol directo todo el día pueden durar algo menos (6-8 años) porque la malla se reseca. El mantenimiento es mínimo: una limpieza al año con agua y jabón neutro. Damos 2 años de garantía en todas nuestras mosquiteras.",
          },
          {
            question: "¿Las mosquiteras oscurecen o quitan luz?",
            answer: "Muy poco. La malla de fibra de vidrio estándar tiene un porcentaje de transparencia superior al 70%, así que apenas se nota. Si buscas aún más transparencia, tenemos mallas de alta transparencia (+85%) que son casi invisibles. En cambio, si quieres además un poco de protección solar, ofrecemos mallas screen que filtran parte de la luz y reducen el calor.",
          },
        ]}
      />

      <ComoTrabajamosSection />

      <section className="py-10 md:py-14 bg-gradient-to-r from-primary-500 to-primary-600">
        <div className="px-4 md:px-6 max-w-4xl mx-auto text-center">
          <h3 className="text-xl md:text-2xl font-bold text-background-50 mb-3 font-heading">Disfruta del aire sin insectos</h3>
          <p className="text-sm md:text-base text-background-50/80 mb-6">Medición gratis · Instalación rápida · Presupuesto sin compromiso</p>
          <a href="tel:+34951120770" className="inline-flex items-center justify-center gap-2 bg-background-50 hover:bg-background-100 text-primary-600 font-semibold text-sm px-8 py-4 rounded-full transition-all whitespace-nowrap cursor-pointer">
            <span className="w-5 h-5 flex items-center justify-center"><i className="ri-phone-fill text-base"></i></span>
            951 12 07 70
          </a>
        </div>
      </section>

      <PricingSection ids={preciosPorServicio["mosquiteras"]} />

      <TrustBannerSection />
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}